/**
 * Keeps Shop Pay Installments visible under the price on VO Product Options pages.
 *
 * VO injects #bcpo into the installment form and pushes shopify-payment-terms below
 * the options. We reorder the payment-terms element above #bcpo without moving #bcpo
 * itself — moving #bcpo into a new form breaks VO conditional logic (e.g. engraving text).
 */
(function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  function bootstrap() {
    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return;

    const paymentTermsBlock = productInfo.querySelector('[data-block-type="payment-terms"]');
    if (!paymentTermsBlock) return;

    const observer = new MutationObserver(() => {
      if (init()) observer.disconnect();
    });

    if (!init()) {
      observer.observe(productInfo, { childList: true, subtree: true });
    }
  }

  function init() {
    const bcpo = document.querySelector('#bcpo');
    if (!bcpo) return false;

    const productInfo = bcpo.closest('.product-info');
    if (!productInfo) return false;

    const paymentTermsBlock = productInfo.querySelector('[data-block-type="payment-terms"]');
    if (!paymentTermsBlock) return false;

    const paymentTermsEl = paymentTermsBlock.querySelector('shopify-payment-terms');
    const installmentForm = paymentTermsBlock.querySelector('payment-terms form, form.shopify-product-form');
    if (!paymentTermsEl || !installmentForm) return false;

    if (productInfo.dataset.voPaymentTermsReady === 'true') return true;
    productInfo.dataset.voPaymentTermsReady = 'true';

    // Clean up leftover host from earlier fix versions that relocated #bcpo
    const legacyHost = productInfo.querySelector('.vo-options-host');
    if (legacyHost && !legacyHost.contains(bcpo)) {
      legacyHost.remove();
    }

    const originalMeta = JSON.parse(paymentTermsEl.getAttribute('shopify-meta') || '{}');
    let addonTotalDollars = 0;
    let syncTimer = null;
    let layoutTimer = null;

    function getInstallmentForm() {
      return paymentTermsBlock.querySelector('payment-terms form, form.shopify-product-form');
    }

    function getPaymentTermsEl() {
      return (
        paymentTermsBlock.querySelector('shopify-payment-terms') ||
        productInfo.querySelector('shopify-payment-terms')
      );
    }

    function getVariantInput() {
      return getInstallmentForm()?.querySelector('[name="id"]');
    }

    /**
     * Keep installment messaging above VO options without relocating #bcpo.
     */
    function applyLayout() {
      const form = getInstallmentForm();
      const termsEl = getPaymentTermsEl();
      if (!form || !termsEl) return;

      let variantInput = form.querySelector('[name="id"]');
      if (!variantInput) {
        variantInput = document.createElement('input');
        variantInput.type = 'hidden';
        variantInput.name = 'id';
        form.prepend(variantInput);
      }

      // Place payment terms immediately after the variant id (above #bcpo)
      if (termsEl.previousElementSibling !== variantInput || !form.contains(termsEl)) {
        variantInput.insertAdjacentElement('afterend', termsEl);
      }

      const priceBlock = productInfo.querySelector('[data-block-type="price"]');
      if (priceBlock && priceBlock.nextElementSibling !== paymentTermsBlock) {
        priceBlock.insertAdjacentElement('afterend', paymentTermsBlock);
      }

      paymentTermsBlock.style.setProperty('display', 'block', 'important');
      termsEl.style.setProperty('display', 'block', 'important');
      termsEl.style.setProperty('visibility', 'visible', 'important');
      termsEl.style.setProperty('opacity', '1', 'important');
    }

    function scheduleLayout() {
      clearTimeout(layoutTimer);
      layoutTimer = setTimeout(applyLayout, 0);
    }

    function scheduleSync(options) {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => syncPaymentTerms(options), 50);
    }

    function getBcpoVariantId() {
      return document.querySelector('#bcpo-select')?.value;
    }

    function parseMoneyToCents(text) {
      if (!text) return null;
      const match = String(text).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      if (!match) return null;
      return Math.round(parseFloat(match[1]) * 100);
    }

    function formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${amount}`;
    }

    function getVariantFromMeta(variantId, meta) {
      return meta.variants?.find((v) => String(v.id) === String(variantId));
    }

    function getVariantBaseCents(variantId) {
      const variant = getVariantFromMeta(variantId, originalMeta);
      if (!variant?.full_price) return null;
      return parseMoneyToCents(variant.full_price.replace(/<[^>]*>/g, ''));
    }

    function getDisplayedTotalCents() {
      const priceBlock = productInfo.querySelector('[data-block-type="price"]');
      const saleEl = priceBlock?.querySelector('.price--sale') || priceBlock?.querySelector('sale-price');
      return parseMoneyToCents(saleEl?.textContent);
    }

    function syncPaymentTerms(options = {}) {
      applyLayout();

      const termsEl = getPaymentTermsEl();
      const variantInput = getVariantInput();
      if (!termsEl || !variantInput) return;

      const variantId = getBcpoVariantId();
      if (!variantId) return;

      if (typeof options.addonTotalDollars === 'number') {
        addonTotalDollars = options.addonTotalDollars;
      }

      termsEl.setAttribute('shopify-meta', JSON.stringify(originalMeta));

      const baseCents = getVariantBaseCents(variantId);
      let totalCents = baseCents;

      if (addonTotalDollars > 0 && baseCents != null) {
        totalCents = baseCents + Math.round(addonTotalDollars * 100);
      } else {
        const displayed = getDisplayedTotalCents();
        if (displayed != null && baseCents != null && displayed !== baseCents) {
          totalCents = displayed;
        }
      }

      if (totalCents != null && baseCents != null && totalCents !== baseCents) {
        const meta = JSON.parse(termsEl.getAttribute('shopify-meta') || '{}');
        const variant = getVariantFromMeta(variantId, meta);
        if (variant) {
          variant.full_price = `<span class=transcy-money>${formatMoney(totalCents)}</span>`;
          if (variant.number_of_payment_terms) {
            const perTermCents = Math.round(totalCents / variant.number_of_payment_terms);
            variant.price_per_term = `<span class=transcy-money>${formatMoney(perTermCents)}</span>`;
          }
          termsEl.setAttribute('shopify-meta', JSON.stringify(meta));
        }
      }

      if (variantInput.value !== variantId) {
        variantInput.value = variantId;
      }

      termsEl.setAttribute('variant-id', variantId);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Fallback for VO conditional text fields (engraving).
     * Shows the next .bcpo-text block when its preceding checkbox group has a selection.
     */
    function syncConditionalTextFields() {
      bcpo.querySelectorAll('.selector-wrapper.bcpo-checkboxes').forEach((group) => {
        const checked = Boolean(group.querySelector('input[type="checkbox"]:checked'));
        let sibling = group.nextElementSibling;

        while (
          sibling &&
          !sibling.classList.contains('bcpo-checkboxes') &&
          !sibling.classList.contains('bcpo-default')
        ) {
          if (sibling.classList.contains('bcpo-text')) {
            const label = sibling.querySelector('.bcpo-label');
            const input = sibling.querySelector('input[type="text"], textarea');
            const fieldWrap = input?.parentElement;

            if (label) label.style.display = checked ? '' : 'none';
            if (fieldWrap && fieldWrap !== label) fieldWrap.style.display = checked ? '' : 'none';
            if (input) {
              input.disabled = !checked;
              if (!checked) input.value = '';
            }
          }
          sibling = sibling.nextElementSibling;
        }
      });
    }

    applyLayout();

    [100, 300, 600, 1000].forEach((delay) => {
      setTimeout(() => {
        applyLayout();
        syncPaymentTerms();
      }, delay);
    });

    document.querySelector('#bcpo-select')?.addEventListener('change', () => {
      addonTotalDollars = 0;
      scheduleSync();
    });

    bcpo.querySelectorAll('.single-option-selector').forEach((select) => {
      select.addEventListener('change', () => {
        addonTotalDollars = 0;
        scheduleSync();
      });
    });

    bcpo.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        // Run after VO's own handlers so we can restore visibility if they fail
        setTimeout(syncConditionalTextFields, 0);
        scheduleSync();
      });
    });

    function onAddonPriceChange(_event, pricingTotal) {
      const addon = typeof pricingTotal === 'number' ? pricingTotal : parseFloat(pricingTotal) || 0;
      scheduleSync({ addonTotalDollars: addon });
    }

    if (window.jQuery) {
      window.jQuery(document.body).on('productOptionsPriceChange', onAddonPriceChange);
    }

    document.body.addEventListener('productOptionsPriceChange', (event) => {
      const detail = event.detail;
      const addon = Array.isArray(detail) ? detail[0] : detail?.pricingTotal ?? detail;
      onAddonPriceChange(event, addon);
    });

    const priceBlock = productInfo.querySelector('[data-block-type="price"]');
    if (priceBlock) {
      new MutationObserver(() => scheduleSync()).observe(priceBlock, {
        subtree: true,
        characterData: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-amount', 'data-usd'],
      });
    }

    const form = getInstallmentForm();
    if (form) {
      new MutationObserver(() => scheduleLayout()).observe(form, {
        childList: true,
      });
    }

    syncConditionalTextFields();
    scheduleSync();
    return true;
  }
})();
